import React from "react";
import { toast } from "react-toastify";

import { Button } from "../../Components/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../../Components/Card";
import { createManualInvoicePdf, ManualInvoiceRequest } from "../../DataService/InvoiceService";

const emptyForm = (): ManualInvoiceRequest => ({
  recipientName: "",
  email: "",
  phone: "",
  address: "",
  postalCode: "",
  city: "",
  description: "",
  quantity: 1,
  unitPrice: 0,
  vatRate: 0.25,
  currency: "SEK",
  dueAt: "",
  notes: ""
});

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const AdminInvoiceForm = () => {
  const [form, setForm] = React.useState<ManualInvoiceRequest>(emptyForm());
  const [saving, setSaving] = React.useState(false);

  const update = <K extends keyof ManualInvoiceRequest>(key: K, value: ManualInvoiceRequest[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!form.recipientName.trim() || !form.email.trim() || !form.description.trim()) {
      toast.error("Fyll i mottagare, e-post och beskrivning.");
      return;
    }

    try {
      setSaving(true);
      const { blob, filename } = await createManualInvoicePdf({
        ...form,
        recipientName: form.recipientName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        postalCode: form.postalCode.trim(),
        city: form.city.trim(),
        description: form.description.trim(),
        notes: form.notes?.trim() || undefined,
        currency: form.currency?.trim() || "SEK",
        dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : undefined,
      });

      downloadBlob(blob, filename);
      toast.success("Faktura skapad och nedladdad.");
      setForm(emptyForm());
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "Kunde inte skapa fakturan.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card disableHover className="admin-surface-card">
      <CardHeader>
        <CardTitle>Skapa manuell faktura</CardTitle>
      </CardHeader>
      <CardContent>
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <label className="admin-field">
              <span>Mottagare</span>
              <input value={form.recipientName} onChange={(e) => update("recipientName", e.target.value)} />
            </label>
            <label className="admin-field">
              <span>E-post</span>
              <input value={form.email} onChange={(e) => update("email", e.target.value)} />
            </label>
            <label className="admin-field">
              <span>Telefon</span>
              <input value={form.phone} onChange={(e) => update("phone", e.target.value)} />
            </label>
            <label className="admin-field">
              <span>Förfallodatum</span>
              <input type="date" value={form.dueAt ?? ""} onChange={(e) => update("dueAt", e.target.value)} />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <label className="admin-field">
              <span>Adress</span>
              <input value={form.address} onChange={(e) => update("address", e.target.value)} />
            </label>
            <label className="admin-field">
              <span>Postnummer</span>
              <input value={form.postalCode} onChange={(e) => update("postalCode", e.target.value)} />
            </label>
            <label className="admin-field">
              <span>Stad</span>
              <input value={form.city} onChange={(e) => update("city", e.target.value)} />
            </label>
            <label className="admin-field">
              <span>Valuta</span>
              <input value={form.currency ?? "SEK"} onChange={(e) => update("currency", e.target.value)} />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            <label className="admin-field">
              <span>Beskrivning</span>
              <input value={form.description} onChange={(e) => update("description", e.target.value)} />
            </label>
            <label className="admin-field">
              <span>Antal</span>
              <input type="number" min="1" step="1" value={form.quantity} onChange={(e) => update("quantity", Number(e.target.value))} />
            </label>
            <label className="admin-field">
              <span>À-pris</span>
              <input type="number" min="0" step="0.01" value={form.unitPrice} onChange={(e) => update("unitPrice", Number(e.target.value))} />
            </label>
            <label className="admin-field">
              <span>Moms</span>
              <input type="number" min="0" step="0.01" value={form.vatRate} onChange={(e) => update("vatRate", Number(e.target.value))} />
            </label>
          </div>

          <label className="admin-field">
            <span>Notering</span>
            <textarea value={form.notes ?? ""} onChange={(e) => update("notes", e.target.value)} />
          </label>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button text={saving ? "Skapar..." : "Skapa PDF"} onClick={() => void handleSubmit()} disabled={saving} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
